import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { TimeTable } from '@app/core/interfaces/c5/time-table';
import { RouterService } from '@app/routing/router.service';
import { Utils } from '@app/shared/services/utils.service';
import { Identifier } from '@app/utils/common';
import { wrapError } from '@app/utils/rxjs';
import { Observable } from 'rxjs';
import { mergeMap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class TimeTableRepository {
  constructor(
    private http: HttpClient,
    private routerService: RouterService,
    private utils: Utils
  ) {}

  create(timeTable: TimeTable): Observable<TimeTable> {
    return this.getUrl().pipe(
      mergeMap((url) => this.http.post<TimeTable>(url, timeTable)),
      wrapError('time-table-repository:create')
    );
  }

  delete(id: number): Observable<void> {
    return this.getUrl(id).pipe(
      mergeMap((url) => this.http.delete<void>(url)),
      wrapError('time-table-repository:delete')
    );
  }

  list(enterpriseId: string, userId?: string): Observable<TimeTable[]> {
    let url: string;

    // End-User level
    if (userId != null) {
      url = '/timeTable';
    } else {
      url = '/timeTables';
    }

    return this.utils.apiUrl(url, { enterpriseId, userId }).pipe(
      mergeMap((url) => this.http.get<TimeTable[]>(url)),
      wrapError('time-table-repository:list')
    );
  }

  listForSite(siteId: number): Observable<TimeTable[]> {
    return this.utils.apiUrl(`/timeTables?siteIdOrName=${siteId}`).pipe(
      mergeMap((url) => this.http.get<TimeTable[]>(url)),
      wrapError('time-table-repository:list-for-site')
    );
  }

  listForUser(userId: number): Observable<TimeTable[]> {
    return this.utils.apiUrl(`/timeTables?userId=${userId}`).pipe(
      mergeMap((url) => this.http.get<TimeTable[]>(url)),
      wrapError('time-table-repository:list-for-user')
    );
  }

  update(timeTable: TimeTable): Observable<void> {
    return this.getUrl(timeTable.id).pipe(
      mergeMap((url) => this.http.put<void>(url, timeTable)),
      wrapError('time-table-repository:update')
    );
  }

  /**
   * Get the url based on the current menu level (this is because the endpoint names are not the same).
   *
   * @param id Optional id of a specific time-table
   */
  private getUrl(id?: Identifier): Observable<string> {
    let url: string;

    // End-User level
    if (this.routerService.extractParameters().urlParameters['userId'] != null) {
      url = '/timeTable';
    } else {
      url = '/timeTables';
    }

    if (id != null) {
      url += `/${id}`;
    }

    return this.utils.apiUrl(url);
  }
}
